import React, { useEffect } from 'react'
import { Form, Input, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { AnswerInput } from '../api'
import { useSaveAnswer } from '../hooks'
import { SiteAnswer } from '../site'
import { addValidationOnError } from '../../util'

export type AnswerModalProps = {
    siteId: string
    /** The visitor's question, for a new answer. */
    question?: string
    /** An existing answer to change. */
    answer?: SiteAnswer
    open: boolean
    onClose: () => void
}

/**
 * Adds an answer Evaa can use for a question it could not answer from the website.
 */
export function AnswerModal({ siteId, question, answer, open, onClose }: AnswerModalProps) {
    const { t } = useTranslation()
    const [form] = Form.useForm<AnswerInput>()
    const save = useSaveAnswer(siteId)

    useEffect(() => {
        if (open) {
            form.setFieldsValue({ question: answer?.question ?? question ?? '', answer: answer?.answer ?? '' })
        }
    }, [open, answer, question, form])

    return (
        <Modal
            title={answer ? t('Change answer') : t('Add answer')}
            open={open}
            onCancel={onClose}
            okText={t('Save')}
            cancelText={t('Cancel')}
            confirmLoading={save.isPending}
            onOk={() => form.submit()}
            destroyOnHidden
        >
            <Typography.Paragraph type="secondary">
                {t(
                    'Evaa uses this answer from now on, for this question and questions like it. It does not change your website.'
                )}
            </Typography.Paragraph>
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={(input) =>
                    save.mutateAsync({ input, answerId: answer?.id }).then(onClose).catch(addValidationOnError(form))
                }
            >
                <Form.Item
                    name="question"
                    label={t('Question')}
                    rules={[{ required: true, message: t('Enter the question') }]}
                >
                    <Input maxLength={500} />
                </Form.Item>
                <Form.Item
                    name="answer"
                    label={t('Answer')}
                    rules={[{ required: true, message: t('Enter the answer') }]}
                >
                    <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        maxLength={5000}
                        placeholder={t(
                            'Yes, we pay half of the childcare costs. Ask your recruiter about the details.'
                        )}
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}
